import { BusinessObjectBase } from './base';
import { pendingStateUpdateSchema } from '@schemas/business-objects/pending-state-update';
import type { ProcessInstance } from './process-instance';
import type { Tenant } from './tenant';
import type { Id } from '../base-types';

export interface PendingStateUpdateData {
  readonly id: Id;
  readonly triggerId?: Id;
  readonly timestamp?: Date;
  readonly processInstanceId: Id;
  readonly processInstance?: ProcessInstance;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface PendingStateUpdate extends Readonly<PendingStateUpdateData> {}
type PendingStateUpdateCreateWithChildrenData = Partial<PendingStateUpdateData>;

export class PendingStateUpdate extends BusinessObjectBase {
  static readonly entityName = 'PendingStateUpdate' as const;
  static readonly plural = 'pending-state-updates';
  static schema = pendingStateUpdateSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<PendingStateUpdate[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new PendingStateUpdate(data));
  }

  static async findOne(filter?: any): Promise<PendingStateUpdate | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new PendingStateUpdate(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<PendingStateUpdate> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new PendingStateUpdate(data);
  }

  static async create(
    data: Omit<PendingStateUpdateData, 'id'>,
  ): Promise<PendingStateUpdate> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PendingStateUpdate(result);
  }

  static async createMany(
    data: Omit<PendingStateUpdateData, 'id'>[],
  ): Promise<PendingStateUpdate[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PendingStateUpdate(item));
  }

  static async createWithChildren(
    data: PendingStateUpdateCreateWithChildrenData,
  ): Promise<PendingStateUpdate> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new PendingStateUpdate(result);
  }

  static async createManyWithChildren(
    data: PendingStateUpdateCreateWithChildrenData[],
  ): Promise<PendingStateUpdate[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PendingStateUpdate(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<PendingStateUpdateData>,
  ): Promise<PendingStateUpdate> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new PendingStateUpdate(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
